import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drives a horizontally-scrolling rail from a pair of arrow buttons.
 *
 * Built on native overflow scrolling rather than a transform-based carousel:
 * touch swipe, trackpad, keyboard and scroll-snap all come for free, and the
 * arrows just nudge scrollLeft. `canPrev`/`canNext` let the caller disable the
 * buttons at either end.
 */
export default function useScrollRail() {
  const railRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const sync = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 8);
    // 8px of slack absorbs sub-pixel rounding at the end of the track.
    setCanNext(el.scrollLeft < maxScroll - 8);
  }, []);

  const scrollBy = useCallback((direction) => {
    const el = railRef.current;
    if (!el) return;
    // Advance by one card plus its gap, inferred from the first child so the
    // step stays correct across breakpoints.
    const card = el.querySelector('[data-rail-item]');
    const step = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;

    sync();
    el.addEventListener('scroll', sync, { passive: true });

    // Card widths are viewport-relative, so the end-of-track test has to be
    // recomputed on resize, not just on scroll.
    const observer = new ResizeObserver(sync);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', sync);
      observer.disconnect();
    };
  }, [sync]);

  return {
    railRef,
    canPrev,
    canNext,
    scrollPrev: () => scrollBy(-1),
    scrollNext: () => scrollBy(1),
    sync,
  };
}
